import React from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import { TEST_SCOPE_KINDS, TEST_SCOPE_LABELS } from './testRecordModel';
import FromReportMark from './FromReportMark';
import UnsavedChangeNote from 'src/components/UI/input/UnsavedChangeNote';
import { unsavedFieldSx } from 'src/components/UI/input/unsavedChange';
import AnchorMark from 'src/components/lab-report-verification/AnchorMark';
import {
  highlightSx,
  useVerification
} from 'src/components/lab-report-verification/useVerificationView';
import { fieldTarget } from 'src/components/lab-report-verification/verificationTargets';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const TARGET = fieldTarget('testScope');

// The scope of a Test as a whole (TGCLOUD-1866, Phase 2 rework): a panel, a targeted or single-site
// test, an exome, a genome. An exome or a genome is recorded by its scope rather than as thousands
// of gene rows, so "genes tested" lists only the genes a report names. The kind is a choice; the
// text beside it is the test's scope as the lab printed it, kept verbatim.
//
// Blank is "not recorded" (null on the Test), never a default kind.
//
// While a lab report is checked beside the Test (TGCLOUD-1868, plan D3), a scope the report gave
// says where it prints it (AnchorMark), and is ringed while the report's selection is about it.

/**
 * @param {object} props
 * @param {{ kind: string, asPrinted?: string }|null|undefined} props.value
 * @param {(value: object|null) => void} props.onChange
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.fromReport]
 * @param {boolean} [props.unsaved]  the scope differs from the Test as saved while a lab report is
 *   applied: drawn as the data entry grid draws an unsaved change
 */
export default function TestScopeField({
  value,
  onChange,
  readOnly = false,
  fromReport = false,
  unsaved = false
}) {
  const t = useAutoTranslation();

  const kind = value?.kind || '';
  const asPrinted = value?.asPrinted || '';
  const view = useVerification();
  const ring = highlightSx(
    !!view && view.active && view.highlighted.has(TARGET)
  );
  return (
    <>
      <Grid
        size={{ xs: 12, sm: 5 }}
        data-verification-target={TARGET}
        sx={ring}
      >
        <TextField
          select
          fullWidth
          label={t('Test scope')}
          value={kind}
          disabled={readOnly}
          onChange={(e) => {
            const next = e.target.value;
            if (!next) onChange(null);
            else
              onChange(asPrinted ? { kind: next, asPrinted } : { kind: next });
          }}
          helperText={fromReport ? undefined : ' '}
          sx={unsavedFieldSx(unsaved)}
        >
          <MenuItem value="">{t('Not recorded')}</MenuItem>
          {TEST_SCOPE_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {t(TEST_SCOPE_LABELS[k])}
            </MenuItem>
          ))}
        </TextField>
        {fromReport && <FromReportMark sx={{ mt: 0.5 }} />}
        <AnchorMark target={TARGET} label={t('Test scope')} sx={{ mt: 0.5 }} />
        {unsaved && <UnsavedChangeNote />}
      </Grid>
      <Grid size={{ xs: 12, sm: 7 }} sx={ring}>
        <TextField
          fullWidth
          label={t('Test scope, as printed')}
          value={asPrinted}
          disabled={readOnly || !kind}
          sx={unsavedFieldSx(unsaved)}
          onChange={(e) => {
            const text = e.target.value;
            onChange(text ? { kind, asPrinted: text } : { kind });
          }}
          helperText={
            kind
              ? t('Optional: the scope in the report’s own words.')
              : t('Choose a scope first.')
          }
        />
      </Grid>
    </>
  );
}
